'use client';

import { motion, AnimatePresence } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

/**
 * SessionRestoreDialog Component
 * Displays options for restoring session from cloud or using local session
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether dialog is open
 * @param {string} props.mode - Dialog mode: 'equal', 'differ', or 'cloud_failed'
 * @param {Function} props.onUseCloud - Callback when user chooses cloud session
 * @param {Function} props.onUseLocal - Callback when user chooses local session
 * @param {Function} props.onClose - Callback to close dialog
 * @param {any} props.cloudData - Cloud session data (for preview/comparison)
 * @param {any} props.localData - Local session data (for preview/comparison)
 */
export default function SessionRestoreDialog({
  isOpen,
  mode,
  onUseCloud,
  onUseLocal,
  onClose,
  cloudData = null,
  localData = null,
  error = null
}) {
  const t = useTranslations('wizard.sessionRestore');
  const [isProcessing, setIsProcessing] = useState(false);

  const handleUseCloud = async () => {
    setIsProcessing(true);
    try {
      await onUseCloud();
    } catch (error) {
      console.error('Error using cloud session:', error);
      setIsProcessing(false);
    }
  };

  const handleUseLocal = async () => {
    setIsProcessing(true);
    try {
      await onUseLocal();
    } catch (error) {
      console.error('Error using local session:', error);
      setIsProcessing(false);
    }
  };

  // Get dialog content based on mode
  const getDialogContent = () => {
    switch (mode) {
      case 'equal':
        return {
          title: t('equal.title'),
          description: t('equal.description'),
          cloudButton: t('equal.cloudButton'),
          localButton: t('equal.localButton'),
          showBothButtons: true
        };

      case 'differ':
        return {
          title: t('differ.title'),
          description: t('differ.description'),
          cloudButton: t('differ.cloudButton'),
          localButton: t('differ.localButton'),
          showBothButtons: true,
          showPreview: true
        };

      case 'cloud_failed':
        return {
          title: t('cloudFailed.title'),
          description: error
            ? t('cloudFailed.descriptionWithError', { error })
            : t('cloudFailed.description'),
          localButton: t('cloudFailed.localButton'),
          showBothButtons: false
        };

      default:
        return {
          title: t('default.title'),
          description: t('default.description'),
          cloudButton: t('default.cloudButton'),
          localButton: t('default.localButton'),
          showBothButtons: true
        };
    }
  };

  const content = getDialogContent();

  // Show session preview info
  const getSessionInfo = (sessionData) => {
    if (!sessionData || !sessionData.state) return null;

    return {
      step: sessionData.state.currentStep || 1,
      hasName: !!sessionData.state.formData?.name,
      hasEmail: !!sessionData.state.formData?.email
    };
  };

  const cloudInfo = getSessionInfo(cloudData);
  const localInfo = getSessionInfo(localData);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white rounded-lg w-full max-w-[560px] p-6 shadow-xl text-[#2B2B2B]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <div className="flex justify-end cursor-pointer mb-2" onClick={onClose}>
              <svg
                width="13"
                height="12"
                viewBox="0 0 13 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <line x1="1.3656" y1="10.7758" x2="11.5694" y2="0.571964" stroke="#8F8F8F" strokeWidth="1.21982" />
                <line x1="1.43127" y1="0.574586" x2="11.6351" y2="10.7784" stroke="#8F8F8F" strokeWidth="1.21982" />
              </svg>
            </div>

            {/* Content */}
            <div className="text-center">
              <h2 className="text-xl md:text-2xl font-semibold text-[#5A4A3A] mb-3">
                {content.title}
              </h2>
              <p className="text-[#8B7355] mb-6">
                {content.description}
              </p>

              {/* Show session previews if in 'differ' mode */}
              {content.showPreview && cloudInfo && localInfo && (
                <div className="grid grid-cols-2 gap-4 mb-6 text-left">
                  {/* Cloud Session Info */}
                  <div className="bg-[#F9F6EB] rounded-lg p-4 border border-[#E8DFC3]">
                    <h3 className="font-semibold text-sm text-[#5A4A3A] mb-2">{t('differ.cloudSession')}</h3>
                    <div className="text-xs text-[#8B7355] space-y-1">
                      <p>{t('differ.step')}: {cloudInfo.step}</p>
                      <p>{t('differ.name')}: {cloudInfo.hasName ? '✓' : '✗'}</p>
                      <p>{t('differ.email')}: {cloudInfo.hasEmail ? '✓' : '✗'}</p>
                    </div>
                  </div>

                  {/* Local Session Info */}
                  <div className="bg-[#F9F6EB] rounded-lg p-4 border border-[#E8DFC3]">
                    <h3 className="font-semibold text-sm text-[#5A4A3A] mb-2">{t('differ.localSession')}</h3>
                    <div className="text-xs text-[#8B7355] space-y-1">
                      <p>{t('differ.step')}: {localInfo.step}</p>
                      <p>{t('differ.name')}: {localInfo.hasName ? '✓' : '✗'}</p>
                      <p>{t('differ.email')}: {localInfo.hasEmail ? '✓' : '✗'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className={`flex gap-4 justify-center ${content.showBothButtons ? 'flex-row' : 'flex-col items-center'}`}>
                {content.showBothButtons && (
                  <button
                    onClick={handleUseCloud}
                    disabled={isProcessing}
                    className="bg-[#E8773E] text-white font-semibold w-full sm:w-[160px] h-[46px] rounded-xl justify-center flex items-center shadow-[0px_1.02px_8.12px_4.06px_rgba(130,130,130,0.17)] hover:bg-[#d66b35] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isProcessing ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-white"></div>
                    ) : (
                      content.cloudButton
                    )}
                  </button>
                )}

                <button
                  onClick={handleUseLocal}
                  disabled={isProcessing}
                  className={`${
                    content.showBothButtons
                      ? 'bg-white text-[#E8773E] border-2 border-[#E8773E]'
                      : 'bg-[#E8773E] text-white'
                  } font-semibold w-full sm:w-[160px] h-[46px] rounded-xl justify-center flex items-center shadow-[0px_1.02px_8.12px_4.06px_rgba(130,130,130,0.17)] hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isProcessing ? (
                    <div className={`animate-spin rounded-full h-5 w-5 border-t-2 ${content.showBothButtons ? 'border-[#E8773E]' : 'border-white'}`}></div>
                  ) : (
                    content.localButton
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
